import { useApplicantAPI } from "@certn/api";
import {
  Container,
  GhostButton,
  Layout,
  Loader,
  MAIN_CONTENT_ID,
  PrimaryButton,
  SectionMessage,
  SkipToMainContent,
  Text,
} from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { useCallback, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { COMPLETE_CASES_PATH } from "./constants";
import { useInformationRequests } from "./hooks";
import { InformationRequestHeader } from "./InformationRequestHeader";
import { InformationRequestSavesProvider } from "./InformationRequestSaves";
import { InformationRequestSection } from "./InformationRequestSection";
import { isOutstandingRequest } from "./utils";
import { AppFooter } from "../../components/app-footer";
import { EmptyState } from "../../components/empty-state/EmptyState";
import { useDocumentTitle } from "../../hooks/api/useDocumentTitle";

export function InformationRequestPage() {
  const { caseId = "" } = useParams();
  const { t } = useTranslation();
  useDocumentTitle(t("Information request — Certn"));

  return (
    <InformationRequestSavesProvider>
      <SkipToMainContent />
      <Layout
        layout="applicant/narrow"
        header={<InformationRequestHeader />}
        footer={<AppFooter />}
      >
        <div id={MAIN_CONTENT_ID} tabIndex={-1} className={styles.main}>
          <Container className={styles.container}>
            <InformationRequestContent caseId={caseId} />
          </Container>
        </div>
      </Layout>
    </InformationRequestSavesProvider>
  );
}

function InformationRequestContent({ caseId }: { caseId: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: completeCases } = useApplicantAPI(COMPLETE_CASES_PATH);
  const [unansweredItemIds, setUnansweredItemIds] = useState<string[]>([]);
  const handleItemsAnswered = useCallback(
    (itemIds: string[]) =>
      setUnansweredItemIds((current) =>
        current.filter((id) => !itemIds.includes(id))
      ),
    []
  );
  const handleItemUnanswered = useCallback(
    (itemId: string) =>
      setUnansweredItemIds((current) =>
        current.includes(itemId) ? current : [...current, itemId]
      ),
    []
  );
  const {
    data: requests,
    error,
    isLoading,
    mutate,
  } = useInformationRequests(caseId);

  if (isLoading) {
    return <Loader type="spinner" size="default" />;
  }

  if (error || !requests) {
    return (
      <SectionMessage
        type="danger"
        testID="information-request-error"
        sectionTitle={t("Unable to load your information request")}
        actions={
          <GhostButton size="small" onPress={() => mutate()}>
            <Trans>Try again</Trans>
          </GhostButton>
        }
      >
        <Trans>Please try again later.</Trans>
      </SectionMessage>
    );
  }

  const outstandingRequests = requests.filter(isOutstandingRequest);

  if (!outstandingRequests.length) {
    return (
      <div className={styles.empty}>
        <EmptyState
          illustration="check-todo"
          title={t("You're all caught up.")}
          description={t("You have no information requests to answer.")}
        />
        <PrimaryButton
          size="medium"
          testID="information-request-back-to-dashboard-button"
          onPress={() => navigate("/")}
        >
          <Trans>Return to dashboard</Trans>
        </PrimaryButton>
      </div>
    );
  }

  const customerName = completeCases?.find(
    (completeCase) => completeCase.short_id === caseId
  )?.customer_name;

  return (
    <div className={styles.content}>
      <div className={styles.titles}>
        <Text as="h1" style="form/page-title">
          <Trans>Information request</Trans>
        </Text>
        <Text style="form/body-primary" color="text-weak">
          {customerName
            ? t(
                "Please answer the following questions and provide any additional details where requested for your {{customerName}} application.",
                { customerName }
              )
            : t(
                "Please answer the following questions and provide any additional details where requested."
              )}
        </Text>
      </div>
      {outstandingRequests.map((request) => (
        <InformationRequestSection
          key={request.id}
          caseId={caseId}
          requestId={request.id}
          unansweredItemIds={unansweredItemIds}
          onItemsAnswered={handleItemsAnswered}
          onItemUnanswered={handleItemUnanswered}
          onListRefresh={() => mutate()}
        />
      ))}
    </div>
  );
}

const styles = {
  main: css({
    "&:focus": {
      outline: "none",
    },
  }),
  container: css({
    paddingTop: "var(--space-12)",
    paddingBottom: "var(--space-12)",
  }),
  content: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-8)",
  }),
  titles: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-2)",
  }),
  empty: css({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "var(--space-6)",
  }),
};
